import { type JSX, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clearToken } from '../../services/auth';
import { usePermissions } from '../../contexts/PermissionsContext';
import { permissionMap } from './permissionMap';
import RupeeIcon from '../icons/RupeeIcon';
import './Sidebar.css';

interface NavItem {
    id: string;
    labelKey: string;
    icon: JSX.Element;
    path: string;
}

interface NavGroup {
    titleKey: string;
    items: NavItem[];
}

interface SidebarProps {
    collapsed: boolean;
    onToggle: () => void;
    onLogout?: () => void;
}

/* Monochromatic SVG icons */
const icons = {
    dashboard: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
    ),
    students: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
    ),
    teachers: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
        </svg>
    ),
    attendance: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
            <path d="m9 16 2 2 4-4" />
        </svg>
    ),
    timetable: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
        </svg>
    ),
    gatePass: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="9" y1="15" x2="15" y2="15" />
        </svg>
    ),
    feeStructures: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="18" rx="2" />
            <line x1="2" y1="9" x2="22" y2="9" />
            <line x1="10" y1="9" x2="10" y2="21" />
        </svg>
    ),
    invoices: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="12" y1="18" x2="12" y2="12" />
            <path d="M15 15H9.5a1.5 1.5 0 0 0 0 3h5a1.5 1.5 0 0 1 0 3H9" />
        </svg>
    ),
    collection: (
        <RupeeIcon />
    ),
    reports: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="20" x2="18" y2="10" />
            <line x1="12" y1="20" x2="12" y2="4" />
            <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
    ),
    expenses: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
            <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
            <path d="M18 12a2 2 0 0 0 0 4h4v-4h-4z" />
        </svg>
    ),
    transactions: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="17 1 21 5 17 9" />
            <path d="M3 11V9a4 4 0 0 1 4-4h14" />
            <polyline points="7 23 3 19 7 15" />
            <path d="M21 13v2a4 4 0 0 1-4 4H3" />
        </svg>
    ),
    feeDefaulters: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L14.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
    ),
    expenseCategories: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
            <line x1="7" y1="7" x2="7.01" y2="7" />
        </svg>
    ),
    studentDiscounts: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="8.5" cy="7" r="4" />
            <line x1="18" y1="8" x2="23" y2="13" />
            <line x1="23" y1="8" x2="18" y2="13" />
        </svg>
    ),
    organization: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M9 21V9h6v12" />
            <path d="M3 9h18" />
        </svg>
    ),
    sessions: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
            <line x1="10" y1="14" x2="14" y2="14" />
        </svg>
    ),
    schools: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M9 21V9h6v12" />
            <path d="M3 9h18" />
        </svg>
    ),
    classes: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
        </svg>
    ),
    subjects: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
    ),
    allocations: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 3h5v5" />
            <line x1="21" y1="3" x2="14" y2="10" />
            <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
        </svg>
    ),
    exams: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
        </svg>
    ),
    homework: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </svg>
    ),
    noticeboard: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
    ),
    holidays: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
            <path d="m9 16 2 2 4-4" />
        </svg>
    ),
    marks: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M3 9h18" />
            <path d="M3 15h18" />
            <path d="M9 3v18" />
            <path d="M15 3v18" />
        </svg>
    ),
    results: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
        </svg>
    ),
    fleet: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="3" width="15" height="13" rx="2" />
            <polygon points="16 8 22 12 22 16 16 16" />
            <circle cx="5.5" cy="18.5" r="2.5" />
            <circle cx="18.5" cy="18.5" r="2.5" />
        </svg>
    ),
    routeBuilder: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="6" cy="6" r="3" />
            <circle cx="6" cy="18" r="3" />
            <path d="M6 9v2a2 2 0 0 0 2 2h8a2 2 0 0 1 2 2v1" />
            <circle cx="18" cy="18" r="3" />
        </svg>
    ),
    passengerAlloc: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M20 8v6" />
            <path d="M23 11h-6" />
        </svg>
    ),
    schoolSubjects: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            <path d="M9 7h6" />
            <path d="M9 11h4" />
        </svg>
    ),
    departments: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="7" width="20" height="14" rx="2" />
            <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
            <line x1="12" y1="12" x2="12" y2="16" />
            <line x1="10" y1="14" x2="14" y2="14" />
        </svg>
    ),
    designations: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
            <path d="M17 3l1.5 1.5L21 2" />
        </svg>
    ),
    tasks: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </svg>
    ),
    collapse: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <line x1="9" y1="3" x2="9" y2="21" />
            <polyline points="14 9 17 12 14 15" />
        </svg>
    ),
};

const navGroups: NavGroup[] = [
    {
        titleKey: 'nav.core',
        items: [
            { id: 'dashboard', labelKey: 'nav.dashboard', icon: icons.dashboard, path: '/dashboard' },
            { id: 'students', labelKey: 'nav.students', icon: icons.students, path: '/students' },
            { id: 'staff', labelKey: 'nav.staff', icon: icons.teachers, path: '/staff' },
        ],
    },
    {
        titleKey: 'nav.academics',
        items: [
            { id: 'schools', labelKey: 'nav.schools', icon: icons.schools, path: '/schools' },
            { id: 'classes', labelKey: 'nav.classes', icon: icons.classes, path: '/classes' },
            { id: 'exams', labelKey: 'nav.exams', icon: icons.exams, path: '/exams' },
            { id: 'marks', labelKey: 'nav.marksEntry', icon: icons.marks, path: '/marks' },
            { id: 'results', labelKey: 'nav.viewResults', icon: icons.results, path: '/results' },
            { id: 'homework', labelKey: 'nav.homework', icon: icons.homework, path: '/homework' },
        ],
    },
    {
        titleKey: 'nav.operations',
        items: [
            { id: 'attendance', labelKey: 'nav.attendance', icon: icons.attendance, path: '/attendance' },
            { id: 'timetable', labelKey: 'nav.timetable', icon: icons.timetable, path: '/timetable' },
            { id: 'gatePass', labelKey: 'nav.gatePass', icon: icons.gatePass, path: '/gate-pass' },
            { id: 'noticeboard', labelKey: 'nav.noticeboard', icon: icons.noticeboard, path: '/noticeboard' },
            { id: 'holidays', labelKey: 'nav.holidays', icon: icons.holidays, path: '/holidays' },
            { id: 'tasks', labelKey: 'nav.tasks', icon: icons.tasks, path: '/tasks' },
        ],
    },
    {
        titleKey: 'nav.transport',
        items: [
            { id: 'fleet', labelKey: 'nav.fleet', icon: icons.fleet, path: '/fleet' },
            { id: 'routeBuilder', labelKey: 'nav.routes', icon: icons.routeBuilder, path: '/route-builder' },
            { id: 'passengerAlloc', labelKey: 'nav.passengers', icon: icons.passengerAlloc, path: '/passenger-allocations' },
        ],
    },
    {
        titleKey: 'nav.financials',
        items: [
            { id: 'feeStructures', labelKey: 'nav.feeStructures', icon: icons.feeStructures, path: '/fee-structures' },
            { id: 'invoices', labelKey: 'nav.invoices', icon: icons.invoices, path: '/invoices' },
            { id: 'collection', labelKey: 'nav.collectionDesk', icon: icons.collection, path: '/collection' },
            { id: 'transactions', labelKey: 'nav.transactions', icon: icons.transactions, path: '/transactions' },
            { id: 'feeDefaulters', labelKey: 'nav.feeDefaulters', icon: icons.feeDefaulters, path: '/fee-defaulters' },
            { id: 'expenses', labelKey: 'nav.expenses', icon: icons.expenses, path: '/expenses' },
            { id: 'studentDiscounts', labelKey: 'nav.studentDiscounts', icon: icons.studentDiscounts, path: '/student-discounts' },
            { id: 'expenseCategories', labelKey: 'nav.expenseCategories', icon: icons.expenseCategories, path: '/expense-categories' },
            { id: 'reports', labelKey: 'nav.reports', icon: icons.reports, path: '/reports' },
        ],
    },
    {
        titleKey: 'nav.admin',
        items: [
            { id: 'organization', labelKey: 'nav.organization', icon: icons.organization, path: '/organization' },
            { id: 'departments', labelKey: 'nav.departments', icon: icons.departments, path: '/departments' },
            { id: 'designations', labelKey: 'nav.designations', icon: icons.designations, path: '/designations' },
            { id: 'subjects', labelKey: 'nav.subjects', icon: icons.subjects, path: '/subjects' },
            { id: 'schoolSubjects', labelKey: 'nav.schoolSubjects', icon: icons.schoolSubjects, path: '/school-subjects' },
            { id: 'allocations', labelKey: 'nav.allocations', icon: icons.allocations, path: '/allocations' },
            { id: 'sessions', labelKey: 'nav.sessions', icon: icons.sessions, path: '/sessions' },
        ],
    },
];

/** Build a flat lookup from path → id for active-state matching */
const pathToIdMap: Record<string, string> = {};
navGroups.forEach(g => g.items.forEach(item => { pathToIdMap[item.path] = item.id; }));

export { navGroups, pathToIdMap };

export default function Sidebar({ collapsed, onToggle, onLogout }: SidebarProps) {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { hasAnyPermission } = usePermissions();

    // Derive active sidebar item from current URL path
    const currentPath = '/' + location.pathname.split('/').filter(Boolean)[0];
    const activeId = pathToIdMap[currentPath] || 'dashboard';

    /* ── Per-group expand/collapse ── */
    // Find which group owns the active item so we auto-expand it
    const activeGroupIndex = navGroups.findIndex(g => g.items.some(i => i.id === activeId));

    const [expandedGroups, setExpandedGroups] = useState<Set<number>>(() => {
        // Start with only the active group expanded
        return new Set(activeGroupIndex >= 0 ? [activeGroupIndex] : [0]);
    });

    // When active route changes, ensure its group is expanded
    useEffect(() => {
        if (activeGroupIndex >= 0 && !expandedGroups.has(activeGroupIndex)) {
            setExpandedGroups(prev => new Set(prev).add(activeGroupIndex));
        }
    }, [activeGroupIndex]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleGroup = (index: number) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    };

    return (
        <aside className={`sidebar ${collapsed ? 'sidebar--collapsed' : ''}`}>
            <nav className="sidebar__nav">
                {navGroups.map((group, groupIdx) => {
                    const visibleItems = group.items.filter(item => {
                        const perms = permissionMap[item.id];
                        if (!perms || perms.length === 0) return true;
                        return hasAnyPermission(perms);
                    });
                    if (visibleItems.length === 0) return null;

                    const isExpanded = expandedGroups.has(groupIdx);
                    return (
                        <div className="sidebar__group" key={group.titleKey}>
                            {!collapsed && (
                                <button
                                    className={`sidebar__group-title ${isExpanded ? 'sidebar__group-title--expanded' : ''}`}
                                    onClick={() => toggleGroup(groupIdx)}
                                >
                                    <span>{t(group.titleKey)}</span>
                                    <svg className="sidebar__group-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="6 9 12 15 18 9" />
                                    </svg>
                                </button>
                            )}
                            <div className={`sidebar__group-items ${!collapsed && !isExpanded ? 'sidebar__group-items--collapsed' : ''}`}>
                                <div className="sidebar__group-inner">
                                    {visibleItems.map((item) => {
                                        const label = t(item.labelKey);
                                        return (
                                            <button
                                                key={item.id}
                                                className={`sidebar__item ${activeId === item.id ? 'sidebar__item--active' : ''}`}
                                                onClick={() => navigate(item.path)}
                                                title={collapsed ? label : undefined}
                                            >
                                                <span className="sidebar__icon">{item.icon}</span>
                                                {!collapsed && <span className="sidebar__label">{label}</span>}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </nav>

            {/* Logout */}
            <button
                className="sidebar__logout"
                title={collapsed ? t('nav.logout') : undefined}
                onClick={() => { clearToken(); onLogout ? onLogout() : navigate('/login'); }}
            >
                <span className="sidebar__icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                </span>
                {!collapsed && <span className="sidebar__label">{t('nav.logout')}</span>}
            </button>

            {/* Collapse toggle */}
            <button className="sidebar__toggle" onClick={onToggle} title={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}>
                <span className={`sidebar__toggle-icon ${collapsed ? 'sidebar__toggle-icon--flipped' : ''}`}>
                    {icons.collapse}
                </span>
            </button>
        </aside>
    );
}
